import type { FlightDto, StatsDto } from '@flightlog/shared';
import { useEffect, useMemo, useState } from 'react';
import { countryName } from '../countries';
import { Flag } from './Flag';
import { formatDistance, plural } from '../format';
import { ChevronDownIcon } from '../icons';
import { setBackButton } from '../telegram';
import { FlightCard } from './FlightsView';
import { Achievements, Records } from './Achievements';
import { Passport } from './Passport';
import { YearInFlights } from './YearInFlights';
import { Comparisons, MONTHS_FULL, MonthGrid, YearChart } from './StatsCharts';

type Sheet = 'countries' | 'airports' | 'airlines' | 'years' | null;

const SHEET_TITLE: Record<Exclude<Sheet, null>, string> = {
  countries: 'Страны',
  airports: 'Аэропорты',
  airlines: 'Авиакомпании',
  years: 'По годам',
};

function yearRange(firstDate: string, lastDate: string): string {
  const first = firstDate.slice(0, 4);
  const last = lastDate.slice(0, 4);
  if (!first || !last) return '';
  return first === last ? first : `${first}–${last}`;
}

function initialSheet(): Sheet {
  const value = new URLSearchParams(window.location.search).get('sheet');
  return value === 'countries' || value === 'airports' || value === 'airlines' || value === 'years'
    ? value
    : null;
}

interface Props {
  stats: StatsDto;
  flights: FlightDto[];
  onSelectFlight: (flight: FlightDto) => void;
}

export function StatsView({ stats, flights, onSelectFlight }: Props) {
  const [sheet, setSheet] = useState<Sheet>(initialSheet);
  /** ГГГГ-ММ открытого месяца из сетки «По месяцам». */
  const [month, setMonth] = useState<string | null>(null);

  useEffect(() => {
    if (sheet) {
      setBackButton(true, () => setSheet(null));
    } else if (month) {
      setBackButton(true, () => setMonth(null));
    } else {
      setBackButton(false);
    }
    return () => setBackButton(false);
  }, [sheet, month]);

  const monthFlights = useMemo(
    () =>
      month
        ? flights
            .filter((flight) => flight.depLocalDate.startsWith(month))
            .sort((a, b) => a.scheduledDepUtc.localeCompare(b.scheduledDepUtc))
        : [],
    [flights, month],
  );

  const countries = stats.countriesList ?? [];
  const airports = stats.airportsList ?? [];
  const airlines = stats.airlinesList ?? [];

  const maxCountry = Math.max(1, ...countries.map((item) => item.flights));
  const maxAirport = Math.max(1, ...airports.map((item) => item.flights));
  const maxAirline = Math.max(1, ...airlines.map((item) => item.flights));
  const maxYear = Math.max(1, ...stats.byYear.map((item) => item.flights));

  const subtitle = useMemo(() => {
    if (sheet === 'countries') return `${stats.countries} стран · ${stats.airports} аэропортов`;
    if (sheet === 'airports') return `${stats.airports} аэропортов · ${stats.countries} стран`;
    if (sheet === 'airlines') return `${stats.airlines} авиакомпаний`;
    if (sheet === 'years') return `${stats.years} лет · ${stats.totalFlights} рейсов`;
    return '';
  }, [sheet, stats]);

  if (stats.totalFlights === 0) {
    return (
      <div className="empty">
        <h2>Статистики пока нет</h2>
        <p>Добавь рейсы — и здесь появятся километры, страны и авиакомпании.</p>
      </div>
    );
  }

  return (
    <div className="stack">
      <Passport countries={countries} />

      <Achievements stats={stats} />

      <Comparisons stats={stats} />

      <section className="grid">
        <button
          type="button"
          className="stat-card"
          onClick={() => setSheet('countries')}
          aria-label="Показать страны"
        >
          <b>{stats.countries}</b>
          <span>{plural(stats.countries, ['страна', 'страны', 'стран'])}</span>
          <i className="stat-arrow">›</i>
        </button>

        <button
          type="button"
          className="stat-card"
          onClick={() => setSheet('airports')}
          aria-label="Показать аэропорты"
        >
          <b>{stats.airports}</b>
          <span>{plural(stats.airports, ['аэропорт', 'аэропорта', 'аэропортов'])}</span>
          <i className="stat-arrow">›</i>
        </button>

        <button
          type="button"
          className="stat-card"
          onClick={() => setSheet('airlines')}
          aria-label="Показать авиакомпании"
        >
          <b>{stats.airlines}</b>
          <span>{plural(stats.airlines, ['авиакомпания', 'авиакомпании', 'авиакомпаний'])}</span>
          <i className="stat-arrow">›</i>
        </button>

        <button
          type="button"
          className="stat-card"
          onClick={() => setSheet('years')}
          aria-label="Показать статистику по годам"
        >
          <b>{stats.years}</b>
          <span>{plural(stats.years, ['год', 'года', 'лет'])}</span>
          <i className="stat-arrow">›</i>
        </button>
      </section>

      <Records stats={stats} flights={flights} onSelectFlight={onSelectFlight} />

      <YearInFlights flights={flights} />

      <YearChart stats={stats} />

      <MonthGrid byMonth={stats.byMonth} onOpen={setMonth} />

      <section className="section">
        <h2 className="section-title">Топ маршрутов</h2>
        <div className="routes">
          {(stats.topRoutes ?? []).map((route) => (
            <div className="route-row" key={`${route.depIata}-${route.arrIata}`}>
              <span className="route-codes">
                {route.depIata} → {route.arrIata}
              </span>
              <span className="route-count">{route.flights}×</span>
              <span className="route-km">{formatDistance(route.distanceKm)}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <h2 className="section-title">Типы бортов</h2>
        <div className="chips">
          {(stats.aircraft ?? []).map((item) => (
            <span className="chip chip-soft" key={item.type}>
              {item.type} <b>{item.flights}</b>
            </span>
          ))}
        </div>
      </section>

      <section className="section">
        <h2 className="section-title">Чаще всего</h2>
        <div className="chips">
          {stats.topAirports.map((item) => (
            <span className="chip chip-soft" key={item.iata}>
              {item.iata} · {item.city} <b>{item.flights}</b>
            </span>
          ))}
        </div>
      </section>

      {sheet ? (
        <div className="overlay" role="dialog" aria-modal="true">
          <button type="button" className="overlay-backdrop" aria-label="Закрыть" onClick={() => setSheet(null)} />
          <div className="sheet stats-sheet">
            <div className="sheet-grab" />
            <div className="sheet-top">
              <span className="eyebrow">{SHEET_TITLE[sheet]}</span>
              <button type="button" className="icon-button" onClick={() => setSheet(null)} aria-label="Закрыть">
                <ChevronDownIcon />
              </button>
            </div>
            <p className="sheet-sub">{subtitle}</p>

            <div className="data-list">
              {sheet === 'countries'
                ? countries.map((item) => (
                    <div className="data-row" key={item.code}>
                      <div className="data-main">
                        <span className="data-title">
                          <Flag code={item.code} lazy />
                          {countryName(item.code)}
                        </span>
                        <span className="data-meta">
                          {item.airports}{' '}
                          {plural(item.airports, ['аэропорт', 'аэропорта', 'аэропортов'])} ·{' '}
                          {yearRange(item.firstDate, item.lastDate)}
                        </span>
                      </div>
                      <span className="data-value">{item.flights}</span>
                      <div className="data-track">
                        <div
                          className="data-fill"
                          style={{ width: `${(item.flights / maxCountry) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))
                : null}

              {sheet === 'airports'
                ? airports.map((item) => (
                    <div className="data-row" key={item.iata}>
                      <div className="data-main">
                        <span className="data-title">
                          <b className="iata-badge">{item.iata}</b> {item.city}
                        </span>
                        <span className="data-meta">
                          <Flag code={item.country} lazy />
                          {countryName(item.country)} · {yearRange(item.firstDate, item.lastDate)}
                        </span>
                      </div>
                      <span className="data-value">{item.flights}</span>
                      <div className="data-track">
                        <div
                          className="data-fill"
                          style={{ width: `${(item.flights / maxAirport) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))
                : null}

              {sheet === 'airlines'
                ? airlines.map((item) => (
                    <div className="data-row" key={item.code}>
                      <div className="data-main">
                        <span className="data-title">
                          {item.name} <span className="data-code">{item.code}</span>
                        </span>
                        <span className="data-meta">{yearRange(item.firstDate, item.lastDate)}</span>
                      </div>
                      <span className="data-value">{item.flights}</span>
                      <span className="data-extra">{formatDistance(item.distanceKm)}</span>
                      <div className="data-track">
                        <div
                          className="data-fill"
                          style={{ width: `${(item.flights / maxAirline) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))
                : null}

              {sheet === 'years'
                ? stats.byYear.map((item) => (
                    <div className="data-row" key={item.year}>
                      <div className="data-main">
                        <span className="data-title">{item.year}</span>
                        <span className="data-meta">
                          {item.flights} {plural(item.flights, ['рейс', 'рейса', 'рейсов'])}
                        </span>
                      </div>
                      <span className="data-value">{formatDistance(item.distanceKm)}</span>
                      <div className="data-track">
                        <div
                          className="data-fill"
                          style={{ width: `${(item.flights / maxYear) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))
                : null}
            </div>
          </div>
        </div>
      ) : null}

      {month ? (
        <div className="overlay" role="dialog" aria-modal="true">
          <button type="button" className="overlay-backdrop" aria-label="Закрыть" onClick={() => setMonth(null)} />
          <div className="sheet stats-sheet">
            <div className="sheet-grab" />
            <div className="sheet-top">
              <span className="eyebrow">
                {MONTHS_FULL[Number(month.slice(5, 7)) - 1]} {month.slice(0, 4)}
              </span>
              <button type="button" className="icon-button" onClick={() => setMonth(null)} aria-label="Закрыть">
                <ChevronDownIcon />
              </button>
            </div>
            <p className="sheet-sub">
              {monthFlights.length} {plural(monthFlights.length, ['рейс', 'рейса', 'рейсов'])} ·{' '}
              {formatDistance(monthFlights.reduce((sum, flight) => sum + (flight.distanceKm ?? 0), 0))}
            </p>
            <div className="list">
              {monthFlights.map((flight) => (
                <FlightCard
                  key={flight.id}
                  flight={flight}
                  onSelect={(picked) => {
                    // Карточка рейса открывается поверх; список месяца закрываем, чтобы «назад» вёл в статистику.
                    setMonth(null);
                    onSelectFlight(picked);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
